/* eslint-disable no-nested-ternary, no-restricted-syntax, func-call-spacing, no-spaced-func */
import React, { useState, useEffect } from 'react';

import { useSelector, useDispatch } from 'react-redux';

// Material
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';

// Helpers
import { Order, stableSort, getComparator } from 'helpers/tableSortLabels';
import { formatDate as format } from 'helpers/date';
import { currencyFormat } from 'helpers/currencyFormat';

// Constants
import { REPORT_GROUPINGS } from 'constants/sellingTerminals';

// Actions
import * as UsersActions from 'store/ducks/users/actions';

// Types
import { ApplicationState } from 'store';
import { PaymentInterface } from 'store/ducks/employeesSales/types';

// Styles
import useStyle from './SellingPaymentNavigation.styles';

interface SellingPaymentNavigationProps {
  setTableReference: (arg: any) => void;
  setTableCSV: (arg: any) => void;
  groupings: Array<string>;
  paymentsList?: PaymentInterface[];
  setPaymentOrder?: (order: Order) => void;
  setPaymentOrderBy?: (orderBy: string) => void;
}

type Props = SellingPaymentNavigationProps;

const SellingPaymentNavigation: React.FC<Props> = ({
  setTableReference,
  setTableCSV,
  groupings,
  paymentsList,
  setPaymentOrder,
  setPaymentOrderBy,
}) => {
  const classes = useStyle();
  const dispatch = useDispatch();

  // data formatting
  const initialPaymentsList = useSelector((state: ApplicationState) => {
    const list =
      paymentsList && paymentsList.length
        ? paymentsList
        : state.sellingTerminals.paymentsList;
    if (list.length === 0) return [];
    // if there is no kind of grouping
    if (groupings.length === 0) {
      return list.map((p) => ({
        id: p.id,
        amount: p.amount,
        totalValue: p.totalValue,
        createdAt: p.createdAt,
        description: p.description,
        receivedBy: p.TransportSupervisor && p.TransportSupervisor.name,
        supervisorId: p.supervisorId,
        transportOperatorId: p.transportOperatorId,
        sellingTerminalName: p.SellingTerminal.name,
      }));
    }

    // if there is any kind of grouping
    return list.map(({ amount, totalValue, usedDate, count }) => {
      const ret = { amount, totalValue };
      if (usedDate !== undefined) {
        Object.assign(ret, ret, { usedDate });
      }
      if (count !== undefined) {
        Object.assign(ret, ret, { count });
      }
      return ret;
    });
  });

  const transportOperatorSelected = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorSelected,
  );

  const transportOperatorsList = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorsList,
  );

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('amount');
  const [tableColumns, setTableColumns] = useState<
    Array<{ key: string; name: string }>
  >([]);

  // defining table structure
  useEffect(() => {
    if (groupings.length === 0) {
      setTableColumns([
        { key: 'createdAt', name: 'Data e Hora' },
        { key: 'amount', name: 'Valor' },
        { key: 'description', name: 'Descrição' },
        { key: 'receivedBy', name: 'Recebido por' },
        { key: 'sellingTerminalName', name: 'Rede de venda' },
      ]);
    } else {
      let columns: Array<{ key: string; name: string }> = [];
      if (groupings.includes(REPORT_GROUPINGS.PAYMENT.USED_DATE)) {
        columns = columns.concat([{ key: 'usedDate', name: 'Data' }]);
      }
      if (groupings.includes(REPORT_GROUPINGS.PAYMENT.AMOUNT)) {
        columns = columns.concat([{ key: 'amount', name: 'Valor' }]);
      }
      columns = columns.concat([
        { key: 'totalValue', name: 'Valor total' },
        { key: 'count', name: 'Quantidade' },
      ]);
      setTableColumns(columns);
    }
  }, [groupings]);

  // Formatters
  const formatDate = (date: string) => date && format(date);

  const formatDateTime = (date: Date) =>
    date && new Date(date).toLocaleString('pt-BR');

  const formatAmount = (amount: number | string) =>
    amount &&
    `R$ ${amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

  // Field to Formatter Mapping
  const fieldFormatting = new Map<string, (arg: any) => any>([
    ['createdAt', formatDateTime],
    ['usedDate', formatDate],
    ['amount', formatAmount],
    ['totalValue', formatAmount],
  ]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    if (setPaymentOrder) setPaymentOrder(newOrder);
    if (setPaymentOrderBy) setPaymentOrderBy(property);
  };

  useEffect(() => {
    dispatch(
      UsersActions.listWebUsersRequest({
        body: { _includes: 'operators', _limit: 9999 },
      }),
    );
  }, []);

  // generating the table csv
  useEffect(() => {
    let csv = '';
    if (tableColumns.length > 0 && initialPaymentsList.length > 0) {
      csv = tableColumns.reduce((a, c) => `${a}${a ? ';' : ''}${c.name}`, '');
      if (transportOperatorSelected.type === 1) csv += ';Operação';
      stableSort(initialPaymentsList, getComparator(order, orderBy)).forEach(
        (p: { [x: string]: any }) => {
          csv += '\n';
          tableColumns.forEach((c) => {
            csv += `${c.key === 'amount'
                ? currencyFormat({ value: Number(p[c.key]) || 0 })
                : p[c.key] || ''
              };`;
          });
          if (transportOperatorSelected.type === 1) {
            csv +=
              transportOperatorsList.find(
                (e) => e.id === p?.transportOperatorId,
              )?.name || '---';
          }
        },
      );
    }
    setTableCSV(csv);
    // eslint-disable-next-line
  }, [tableColumns, initialPaymentsList]);

  return (
    <TableContainer
      className={classes.tableContainer}
      ref={(ref: any) => setTableReference(ref)}>
      <Table className={classes.table} component={Paper}>
        <TableHead>
          <TableRow>
            {tableColumns.length > 0 &&
              tableColumns.map((col) => (
                <TableCell
                  key={col.key}
                  variant='head'
                  sortDirection={orderBy === col.key ? order : false}>
                  <TableSortLabel
                    classes={{
                      root: classes.sortLabel,
                      icon: classes.sortLabel,
                    }}
                    active
                    direction={orderBy === col.key ? order : 'asc'}
                    onClick={(event) => handleRequestSort(event, col.key)}>
                    {col.name}
                    {orderBy === col.key ? (
                      <span className={classes.visuallyHidden}>
                        {order === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            {transportOperatorSelected.type === 1 && (
              <TableCell
                variant='head'
                align='left'
                sortDirection={
                  orderBy === 'transportOperatorId' ? order : false
                }>
                <TableSortLabel
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortLabel,
                  }}
                  active
                  direction={orderBy === 'transportOperatorId' ? order : 'asc'}
                  onClick={(event) =>
                    handleRequestSort(event, 'transportOperatorId')
                  }>
                  Operação
                  {orderBy === 'transportOperatorId' ? (
                    <span className={classes.visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {initialPaymentsList.length > 0 &&
            stableSort(initialPaymentsList, getComparator(order, orderBy)).map(
              (payment, index) => (
                <TableRow>
                  {tableColumns.map((col) => {
                    const formatter = fieldFormatting.get(col.key);

                    return (
                      <TableCell>
                        {formatter
                          ? formatter(payment[col.key])
                          : payment[col.key]}
                      </TableCell>
                    );
                  })}
                  {transportOperatorSelected.type === 1 && (
                    <TableCell>
                      {transportOperatorsList.find(
                        (e) => e.id === payment?.transportOperatorId,
                      )?.name || '---'}
                    </TableCell>
                  )}
                </TableRow>
              ),
            )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export { SellingPaymentNavigation };
export default SellingPaymentNavigation;
